// src/hr/timeoff/RequestDrawer.js -- "Request time off" (mockup R1).
// Requestable types only (sick days are reported, protected leave is logged by HR). The preview is a
// client-side estimate -- weekdays minus company holidays -- and the server recounts on submit.
// A refused request keeps the drawer open with the server's own message.
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiAlertCircle, FiArrowRight, FiSend } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Drawer, EmptyState, Field, Seg } from '../kit';
import { plainId } from '../kit/util';
import { daysLabel, daysNumber, dayLabel } from '../format';
import { isDateString, nextWorkday } from './dates';
import { estimateHours, typeLabel } from './labels';

const LENGTHS = [{ value: 8, label: 'Full day' }, { value: 4, label: 'Half day' }];

function holidaySetOf(me) {
  return new Set(((me && me.holidays) || []).map((h) => h.date));
}

function RequestForm({ me, today, onClose, onDone, onReportSick }) {
  const holidays = useMemo(() => holidaySetOf(me), [me]);
  const types = useMemo(() => ((me && me.types) || []).filter((t) => t.requestable && !t.isSick), [me]);
  const firstDay = nextWorkday(today, holidays);

  const [typeId, setTypeId] = useState(types[0] ? types[0].id : '');
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(firstDay);
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const ids = plainId(useId());
  const errorRef = useRef(null);

  // A refusal shows under the form; bring it into view in case the drawer body is scrolled.
  useEffect(() => {
    if (error && errorRef.current && errorRef.current.scrollIntoView) errorRef.current.scrollIntoView({ block: 'nearest' });
  }, [error]);

  const type = types.find((t) => t.id === typeId) || null;
  const balance = ((me && me.balances) || []).find((b) => b.typeId === typeId) || null;
  const yearEnd = me && me.yearRange ? me.yearRange.end : null;
  const datesOk = isDateString(startDate) && isDateString(endDate);
  const ordered = datesOk && endDate >= startDate;
  const hours = ordered ? estimateHours(startDate, endDate, hoursPerDay, holidays) : 0;
  const before = balance ? balance.availableHours : 0;
  const after = before - hours;
  const nextYear = ordered && yearEnd && startDate > yearEnd;
  const holidaysInRange = ordered ? (me.holidays || []).filter((h) => h.date >= startDate && h.date <= endDate) : [];
  const approver = me && me.approverName;
  const notify = (me && me.notifyNames) || [];

  const changeStart = (value) => {
    setStartDate(value);
    if (isDateString(value) && (!isDateString(endDate) || endDate < value)) setEndDate(value);
  };

  const submit = async () => {
    if (busy || !type) return;
    if (!ordered) {
      setError(datesOk ? 'The last day is before the first day' : 'Pick valid dates');
      return;
    }
    const body = { typeId, startDate, endDate, hoursPerDay };
    const trimmed = note.trim();
    if (type.allowNotes && trimmed) body.note = trimmed;
    setBusy(true);
    setError('');
    try {
      await hr.submitRequest(body);
      onDone(`Request sent to ${approver || 'HR'}`);
    } catch (err) {
      setError(errorText(err, "Couldn't send your request. Try again."));
      setBusy(false);
    }
  };

  const footer = types.length > 0 ? (
    <>
      <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      <Btn type="submit" icon={FiSend} loading={busy}>Send request</Btn>
    </>
  ) : <Btn variant="ghost" onClick={onClose}>Close</Btn>;

  return (
    <Drawer open onClose={onClose} title="Request time off" width={400} footer={footer} onSubmit={submit} className="hrp-req">
      {types.length === 0 ? (
        <EmptyState compact title="Nothing to request yet">
          HR hasn't set up a time-off type you can request. Contact HR if you need time off.
        </EmptyState>
      ) : (
        <>
          <div className="hrp-field">
            <div className="hrp-field__label" id={`${ids}-type`}>Type</div>
            <Seg
              labelledBy={`${ids}-type`}
              options={types.map((t) => ({ value: t.id, label: typeLabel(t) }))}
              value={typeId}
              onChange={(value) => { setTypeId(value); setError(''); }}
            />
            {onReportSick && (
              <button type="button" className="hrp-link hrp-req__sick" onClick={onReportSick}>
                Out sick today? Report a sick day <FiArrowRight aria-hidden="true" />
              </button>
            )}
          </div>

          <div className="hrp-two">
            <Field label="First day" type="date" value={startDate} onChange={(e) => changeStart(e.target.value)} required />
            <Field label="Last day" type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} required />
          </div>

          <div className="hrp-field">
            <div className="hrp-field__label" id={`${ids}-len`}>Length per day</div>
            <Seg labelledBy={`${ids}-len`} options={LENGTHS} value={hoursPerDay} onChange={setHoursPerDay} />
          </div>

          {type && type.allowNotes && (
            <Field
              label="Note"
              labelNote="(optional)"
              value={note}
              maxLength={200}
              placeholder="e.g. Holiday travel"
              onChange={(e) => setNote(e.target.value)}
            />
          )}

          <div className="hrp-req__prev" data-testid="hrp-req-preview" aria-live="polite">
            <div className="hrp-lab hrp-req__prevlab">{typeLabel(type)} left to book</div>
            {!ordered && <p className="hrp-req__warn">{datesOk ? 'The last day is before the first day.' : 'Pick the first and last day.'}</p>}
            {ordered && nextYear && (
              <p className="hrp-req__note">This falls in your {Number(me.year) + 1} benefit year, so next year's balance applies.</p>
            )}
            {ordered && !nextYear && (
              <div className="hrp-req__math">
                <span>{daysNumber(before)}</span>
                <span className="hrp-req__arrow" aria-hidden="true">→</span>
                <span className="hrp-sr">becomes</span>
                <b data-neg={after < 0 ? 'true' : undefined}>{daysNumber(after)}</b>
                <span>days if approved</span>
              </div>
            )}
            {ordered && (
              <p className="hrp-req__note">
                {hours > 0 ? `${daysLabel(hours)} requested` : 'Those dates have no workdays'}
                {hours > 0 && ' · weekends and holidays aren’t counted'}
              </p>
            )}
            {holidaysInRange.length > 0 && (
              <p className="hrp-req__note">
                Includes {holidaysInRange.map((h) => `${h.name} (${dayLabel(h.date)})`).join(', ')} — not counted.
              </p>
            )}
            {ordered && !nextYear && after < 0 && <p className="hrp-req__warn">That's more than you have left to book.</p>}
          </div>

          <div className="hrp-req__path">
            <div className="hrp-lab">Who approves this</div>
            <ol className="hrp-req__steps" aria-label="Who approves this">
              {approver && (
                <li>
                  <span className="hrp-req__n" aria-hidden="true">1</span>
                  <span className="hrp-req__step"><b>{approver}</b><em>Your approver</em></span>
                </li>
              )}
              <li>
                <span className="hrp-req__n" aria-hidden="true">{approver ? 2 : 1}</span>
                <span className="hrp-req__step"><b>HR</b><em>{approver ? 'Final sign-off' : 'Reviews and signs off'}</em></span>
              </li>
            </ol>
            {notify.length > 0 && <p className="hrp-req__cc">{`Also notified: ${notify.join(', ')}`}</p>}
          </div>

          {error && (
            <div ref={errorRef} className="hrp-form-err" role="alert">
              <FiAlertCircle aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}

// The form is mounted fresh each time the drawer opens, so it always starts from the defaults.
export function RequestDrawer({ open, ...props }) {
  if (!open) return null;
  return <RequestForm {...props} />;
}

export default RequestDrawer;
